import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import type { RunToolCard } from '../../components/screens/helpers/runScreenTypes';
import { IconCheck } from './appIcons';
import { AppToolMark, MODEL_ROLES } from './AppToolsView';
import { USAGE_TIER_LABELS, getUsageTier, type AppUsageTier } from './appWorkflowUsage';

export interface AppWorkflowPickerGroup {
  id: string;
  title: string;
  options: RunToolCard[];
}

interface AppWorkflowPickerProps {
  anchor: HTMLElement;
  label: string;
  groups: AppWorkflowPickerGroup[];
  selected: string[];
  /** Most options that can be selected; the rest are disabled at the cap but can still be unselected. */
  limit?: number;
  /** States the limit at the top of the list, before anyone runs into it. */
  limitNote?: string;
  /** Explains a disabled option. */
  limitReason?: string;
  onToggle: (id: string) => void;
  onClose: () => void;
}

const PICKER_GAP_PX = 6;
const PICKER_MIN_WIDTH_PX = 280;
const PICKER_MAX_HEIGHT_PX = 420;
const VIEWPORT_MARGIN_PX = 8;

const USAGE_LEVELS: AppUsageTier[] = [1, 2, 3];

/** Three small bars, like a signal meter: more bars use more of the plan, so the workflow runs less often. */
export function AppUsageMeter({ tier }: { tier: AppUsageTier }) {
  return (
    <span className="app-wf-usage" role="img" aria-label={USAGE_TIER_LABELS[tier]} title={USAGE_TIER_LABELS[tier]}>
      {USAGE_LEVELS.map((level) => <span key={level} className={level <= tier ? 'on' : undefined} />)}
    </span>
  );
}

/**
 * Multi-select popover for a phase's models or tools. It stays open while options are toggled
 * and is placed once when it opens: below its trigger and as wide as it, flipped above only when
 * there is more room there, and capped to the space it has so it never needs to move.
 */
function AppWorkflowPicker(props: AppWorkflowPickerProps) {
  const { anchor, label, groups, selected, limit, limitNote, limitReason, onToggle, onClose } = props;
  const pickerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });
  const isAtLimit = limit !== undefined && selected.length >= limit;
  // A limit of one works like a radio group: picking another option replaces the current one.
  const disablesAtLimit = isAtLimit && limit !== 1;

  useLayoutEffect(() => {
    const rect = anchor.getBoundingClientRect();
    const naturalHeight = pickerRef.current?.scrollHeight ?? 0;
    const width = Math.min(Math.max(rect.width, PICKER_MIN_WIDTH_PX), window.innerWidth - VIEWPORT_MARGIN_PX * 2);
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN_PX));
    const spaceBelow = window.innerHeight - rect.bottom - PICKER_GAP_PX - VIEWPORT_MARGIN_PX;
    const spaceAbove = rect.top - PICKER_GAP_PX - VIEWPORT_MARGIN_PX;
    const placeBelow = spaceBelow >= naturalHeight || spaceBelow >= spaceAbove;

    setStyle(placeBelow
      ? { left, width, maxHeight: Math.min(spaceBelow, PICKER_MAX_HEIGHT_PX), top: rect.bottom + PICKER_GAP_PX }
      : { left, width, maxHeight: Math.min(spaceAbove, PICKER_MAX_HEIGHT_PX), bottom: window.innerHeight - rect.top + PICKER_GAP_PX });
    pickerRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }, [anchor]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (!pickerRef.current?.contains(target) && !anchor.contains(target)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        anchor.focus();
      }
    };
    // The popover is fixed, so it would drift from its trigger when the page scrolls.
    const handleScroll = (event: Event) => {
      if (!pickerRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', onClose);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  return (
    <div ref={pickerRef} className="app-menu app-wf-picker" role="menu" aria-label={label} style={style}>
      {limitNote && <p className={`app-wf-picker-note${isAtLimit ? ' full' : ''}`}>{limitNote}</p>}
      {groups.map((group) => (
        <div key={group.id} className="app-wf-picker-group" role="group" aria-label={group.title}>
          {groups.length > 1 && <span className="app-wf-picker-heading" aria-hidden={true}>{group.title}</span>}
          {group.options.map((option) => {
            const isSelected = selected.includes(option.id);
            const isDisabled = disablesAtLimit && !isSelected;

            return (
              <button
                key={option.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={isSelected}
                disabled={isDisabled}
                title={isDisabled ? limitReason : undefined}
                className={`app-wf-picker-option${isSelected ? ' selected' : ''}`}
                onClick={() => onToggle(option.id)}
              >
                <AppToolMark tool={option} />
                <span className="app-wf-picker-copy">
                  <strong>{option.title}</strong>
                  <span>{MODEL_ROLES[option.id] ?? option.tag ?? 'Security tool'}</span>
                </span>
                {option.kind === 'model' && <AppUsageMeter tier={getUsageTier(option.id)} />}
                <span className="app-wf-picker-check" aria-hidden={true}>
                  {isSelected && <IconCheck aria-hidden={true} />}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default AppWorkflowPicker;
